<template>
	<div>
		<Breadcrumbs main="App" title="User Management" />
		<div class="container-fluid">
			<div class="row">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<div>
								<h5>User Management</h5>
								<span>Create, edit, and delete user accounts.</span>
							</div>
							<div class="d-flex gap-2">
								<button class="btn btn-outline-primary" type="button" @click="fetchUsers" :disabled="loading">
									Refresh
								</button>
								<button
									v-if="canCreate"
									class="btn btn-primary"
									type="button"
									@click="openCreate"
								>
									New User
								</button>
							</div>
						</div>

						<div class="card-body">
							<div v-if="errorMessage" class="alert alert-danger" role="alert">
								{{ errorMessage }}
							</div>
							<div v-if="successMessage" class="alert alert-success" role="alert">
								{{ successMessage }}
							</div>

							<div class="mb-3 row align-items-center">
								<label for="user-search" class="col-sm-2 col-form-label">Search</label>
								<div class="col-sm-10">
									<input
										id="user-search"
										v-model="searchTerm"
										class="form-control"
										type="text"
										placeholder="Filter by username"
									/>
								</div>
							</div>

							<div class="table-responsive">
								<table class="table table-striped table-bordered">
									<thead>
										<tr>
											<th>#</th>
											<th>Username</th>
											<th>Role</th>
											<th>Status</th>
											<th class="text-center">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr v-if="loading">
											<td colspan="5" class="text-center">Loading...</td>
										</tr>
										<tr v-else-if="!filteredUsers.length">
											<td colspan="5" class="text-center">No users found.</td>
										</tr>
										<tr v-for="user in filteredUsers" :key="user.id">
											<td>{{ user.id }}</td>
											<td>{{ user.username }}</td>
											<td>
												<span class="badge badge-primary">{{ user.role || user.roleName || '—' }}</span>
											</td>
											<td>{{ user.isLocked ? 'Locked' : 'Active' }}</td>
											<td class="text-center">
												<button
													v-if="canUnlock && user.isLocked"
													class="btn btn-sm btn-outline-warning mr-1"
													@click="unlockUser(user)"
												>
													Unlock
												</button>
												<button
													v-if="canRead"
													class="btn btn-sm btn-outline-primary mr-1"
													@click="viewProfile(user)"
												>
													View Profile
												</button>
												<button
													v-if="canUpdate"
													class="btn btn-sm btn-outline-secondary mr-1"
													@click="openEdit(user)"
												>
													Edit
												</button>
												<button
													v-if="canDelete"
													class="btn btn-sm btn-outline-danger"
													@click="deleteUser(user)"
												>
													Delete
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Create / Edit Modal -->
		<div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
			<div class="modal-dialog-custom card">
				<div class="card-header d-flex justify-content-between align-items-center">
					<h5 class="mb-0">{{ isEditing ? 'Edit User' : 'New User' }}</h5>
					<button type="button" class="close" @click="closeModal">
						<span>&times;</span>
					</button>
				</div>
				<div class="card-body">
					<div v-if="formError" class="alert alert-danger">{{ formError }}</div>
					<form @submit.prevent="saveUser">
						<div class="form-group">
							<label>Username <span class="text-danger">*</span></label>
							<input
								v-model="form.username"
								type="text"
								class="form-control"
								placeholder="Enter username"
								required
							/>
						</div>
						<div class="form-group">
							<label>
								Password
								<small v-if="isEditing" class="text-muted">(leave blank to keep current)</small>
								<span v-else class="text-danger">*</span>
							</label>
							<input
								v-model="form.password"
								type="password"
								class="form-control"
								placeholder="Enter password"
								:required="!isEditing"
							/>
						</div>
						<div class="form-group">
							<label>Role</label>
							<select v-model="form.role" class="form-control">
								<option value="">— Select role —</option>
								<option v-for="role in roles" :key="role.id" :value="role.name">
									{{ role.name }}
								</option>
							</select>
						</div>
						<div class="d-flex justify-content-end gap-2 mt-3">
							<button type="button" class="btn btn-secondary mr-2" @click="closeModal">
								Cancel
							</button>
							<button type="submit" class="btn btn-primary" :disabled="saving">
								{{ saving ? 'Saving...' : (isEditing ? 'Update User' : 'Create User') }}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { API_BASE } from '../../config/api';

export default {
	name: 'UserIndex',

	data() {
		return {
			users: [],
			roles: [],
			loading: false,
			saving: false,
			searchTerm: '',
			errorMessage: '',
			successMessage: '',
			showModal: false,
			isEditing: false,
			editingId: null,
			form: {
				username: '',
				password: '',
				role: '',
			},
			formError: '',
		};
	},

	computed: {
		permissions() {
			return this.$store.state.auth?.permissions || [];
		},
		canCreate() {
			return this.permissions.includes('user:create');
		},
		canUpdate() {
			return this.permissions.includes('user:update');
		},
		canDelete() {
			return this.permissions.includes('user:delete');
		},
		canRead() {
			return this.permissions.includes('user:read');
		},
		canUnlock() {
			return this.permissions.includes('Admin');
		},
		filteredUsers() {
			if (!this.searchTerm.trim()) return this.users;
			const q = this.searchTerm.trim().toLowerCase();
			return this.users.filter(u =>
				(u.username || '').toLowerCase().includes(q)
			);
		},
	},

	mounted() {
		this.fetchUsers();
		this.fetchRoles();
	},

	methods: {
		authHeaders(includeJson = true) {
			const token = localStorage.getItem('token');
			const headers = {};
			if (token) headers['Authorization'] = `Bearer ${token}`;
			if (includeJson) headers['Content-Type'] = 'application/json';
			return headers;
		},

		showSuccess(msg) {
			this.successMessage = msg;
			this.errorMessage = '';
			setTimeout(() => { this.successMessage = ''; }, 4000);
		},

		showError(msg) {
			this.errorMessage = msg;
			this.successMessage = '';
		},

		async fetchUsers() {
			this.loading = true;
			this.errorMessage = '';
			try {
				const res = await fetch(`${API_BASE}/api/User`, { headers: this.authHeaders(false) });
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				const data = await res.json();
				this.users = Array.isArray(data) ? data : (data.data || data.items || []);
			} catch (err) {
				this.showError('Failed to load users: ' + err.message);
			} finally {
				this.loading = false;
			}
		},

		async fetchRoles() {
			try {
				const res = await fetch(`${API_BASE}/api/Role`, { headers: this.authHeaders(false) });
				if (!res.ok) return;
				const data = await res.json();
				const list = Array.isArray(data) ? data : (data.data || data.items || []);
				// Normalise: each entry may have id+name or just a string
				this.roles = list.map(r => (typeof r === 'string' ? { id: r, name: r } : r));
			} catch {
				// roles dropdown will be empty; user can type manually
			}
		},

		openCreate() {
			this.isEditing = false;
			this.editingId = null;
			this.form = { username: '', password: '', role: '' };
			this.formError = '';
			this.showModal = true;
		},

		openEdit(user) {
			this.isEditing = true;
			this.editingId = user.id;
			this.form = {
				username: user.username || '',
				password: '',
				role: user.role || user.roleName || '',
			};
			this.formError = '';
			this.showModal = true;
		},

		closeModal() {
			this.showModal = false;
		},

		async saveUser() {
			this.formError = '';
			this.saving = true;
			try {
				let url, method, body;
				if (this.isEditing) {
					url = `${API_BASE}/api/User/${this.editingId}`;
					method = 'PUT';
					body = { username: this.form.username, role: this.form.role };
					if (this.form.password) body.password = this.form.password;
				} else {
					url = `${API_BASE}/api/User/register`;
					method = 'POST';
					body = { username: this.form.username, password: this.form.password, role: this.form.role };
				}

				const res = await fetch(url, {
					method,
					headers: this.authHeaders(),
					body: JSON.stringify(body),
				});

				if (!res.ok) {
					let errMsg = `HTTP ${res.status}`;
					try {
						const errData = await res.json();
						errMsg = errData.message || errData.title || JSON.stringify(errData);
					} catch { /* non-JSON error body */ }
					this.formError = errMsg;
					return;
				}

				this.closeModal();
				this.showSuccess(this.isEditing ? 'User updated successfully.' : 'User created successfully.');
				await this.fetchUsers();
			} catch (err) {
				this.formError = 'Request failed: ' + err.message;
			} finally {
				this.saving = false;
			}
		},

		async deleteUser(user) {
			if (!confirm(`Delete user "${user.username}"? This cannot be undone.`)) return;
			try {
				const res = await fetch(`${API_BASE}/api/User/${user.id}`, {
					method: 'DELETE',
					headers: this.authHeaders(false),
				});
				if (!res.ok) {
					let errMsg = `HTTP ${res.status}`;
					try {
						const errData = await res.json();
						errMsg = errData.message || errData.title || JSON.stringify(errData);
					} catch { /* ignore */ }
					this.showError('Delete failed: ' + errMsg);
					return;
				}
				this.showSuccess(`User "${user.username}" deleted.`);
				await this.fetchUsers();
			} catch (err) {
				this.showError('Delete failed: ' + err.message);
			}
		},

		async unlockUser(user) {
			if (!confirm(`Unlock user "${user.username}"?`)) return;
			try {
				const res = await fetch(`${API_BASE}/api/User/${user.id}/unlock`, {
					method: 'POST',
					headers: this.authHeaders(false),
				});
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				this.showSuccess(`User "${user.username}" unlocked.`);
				await this.fetchUsers();
			} catch (err) {
				this.showError('Unlock failed: ' + err.message);
			}
		},

		viewProfile(user) {
			this.$router.push({
				name: 'UserProfile',
				params: { userId: String(user.id) },
			});
		},
	},
};
</script>

<style scoped>
.modal-backdrop-custom {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1050;
}
.modal-dialog-custom {
	width: 100%;
	max-width: 520px;
	border-radius: 8px;
	overflow: hidden;
}
</style>
